import { BallType } from '../../enums/machine.enums';
import { BuildPriority, PitchType } from '../../enums/pitches.enums';
import { HasId } from '../common/i-extensions';
import { Nullable } from '../common/i-generics';
import { IDateRangeValue } from '../forms';
import { IMachineState } from '../i-machine-state';
import { IPlateLoc, ITrajectory, ITrajektRefBreak } from '../pitches';
import { IMachineShot } from '../training/i-machine-shot';
import { ITrainedMachineState } from '../training/i-trained-machine-state';

interface IPitchMetadata extends HasId {
  bs_hash: string;
  name: string;
  type: PitchType;
  priority: BuildPriority | undefined;
  _mx_tms: ITrainedMachineState | undefined;
}

export interface IPitchPerformanceFire {
  pitch: IPitchMetadata;
  shot: IMachineShot;
}

export interface IPitchPerfFilters {
  currPeriod: Partial<IDateRangeValue>;
  prevPeriod: Partial<IDateRangeValue>;

  shots: number;
  ball_type: BallType | undefined;
  training: Nullable<boolean>;
  includeLowConfidence: boolean;
}

export interface IPitchPerfResults {
  // aka: HWD shots for HWD report
  current: IGroupedFires[];
  // aka: repeatability shots for HWD report
  previous: IGroupedFires[];
}

export interface IGatherFiresQuery {
  machineID?: string;

  collectionID?: string;

  ball_type?: BallType;

  /** in ISO 8601 format */
  start_date?: string;

  /** in ISO 8601 format */
  end_date?: string;

  /** limit the number of records in the database query */
  limit?: number;

  /** training shots vs. bp shots */
  training?: boolean;
}

// _id will be used for checkable behaviour in table
export interface IGroupedFires extends HasId {
  rgb: string;

  pitch: IPitchMetadata;

  // i.e. does the shot ms matching_hash match the pitch's current ms matching_hash
  isCurrentMs: boolean;

  // max _created from fires
  lastFired: string | undefined;

  target: {
    ms: IMachineState;
    traj: ITrajectory;

    speedMPH: number;
    netSpin: number;
    plate: IPlateLoc;
    break: ITrajektRefBreak;
  };

  average: {
    speedMPH: number;
    netSpin: number;
    plate: IPlateLoc;
    break: ITrajektRefBreak;
  };

  delta: {
    speedMPH: number;
    netSpin: number;
    plate: IPlateLoc;
    break: ITrajektRefBreak;
  };

  fires: IPitchPerformanceFire[];

  // aimed at the plate location of the first fire found
  rotatedShots: IMachineShot[];
}

export interface IShotActuals {
  speedMPH: number;
  break: ITrajektRefBreak;
  plate: IPlateLoc;
}

export const EMPTY_SHOT_ACTUALS: IShotActuals = {
  speedMPH: 0,
  break: {
    xInches: 0,
    zInches: 0,
  },
  plate: {
    plate_x: 0,
    plate_z: 0,
  },
};

export interface IMetricDetails {
  // should always be 100% unless there are 0 samples
  total: number;
  // cannot be higher than the minimum of the other values
  overall: number;
  // passes, not fails
  velocity: number;
  // passes, not fails
  break: number;
  // passes, not fails
  plate: number;
}

export const EMPTY_METRIC_DETAILS: IMetricDetails = {
  total: 0,
  overall: 0,
  velocity: 0,
  break: 0,
  plate: 0,
};

export interface IMetricTally {
  counts: IMetricDetails;
  percents: IMetricDetails;
}

// shown when drilling down into a pitch
export interface IFireWithMetrics extends IShotActuals, HasId {
  target: {
    ms: IMachineState;
    traj: ITrajectory;

    speedMPH: number;
    plate: IPlateLoc;
    break: ITrajektRefBreak;
  };

  delta: {
    speedMPH: number;
    plate: IPlateLoc;
    break: ITrajektRefBreak;
  };

  shot: IMachineShot;
}
